import { InvoiceTable, InvoiceDetails } from "@/types/invoice";
import {
  LinkedMembers,
  LinkedMembersInvoiceDataResponse,
} from "@/types/linked-members";
import { ApiResponse } from "@/types";

import { apiSlice } from "@/redux/apiSlice";

export const Invoice = apiSlice.injectEndpoints({
  endpoints(builder) {
    return {
      getInvoices: builder.query<InvoiceTable, { query: string, org_id: number }>({
        query: ({ query, org_id }) => ({
          url: `/invoices?org_id=${org_id}${query !== "" ? "&" + query : ""}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: ["Invoice", "Transaction", "Members","SysConfig","Facility"],
      }),

      getLinkedMembers: builder.query<
        LinkedMembersInvoiceDataResponse,
        { id: number; query: string }
      >({
        query: ({ id, query }) => ({
          url: `/member/${id}/invoice-linked-members${query !== "" ? "?" + query : ""}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        providesTags: ["Invoice", "Transaction", "Members","SysConfig","Facility"],
      }),

      addLinkedMembers: builder.mutation<
        ApiResponse,
        {
          member_id: number;
          book_date: string;
          membership_plan_id: number;
          contract_id: number;
          invoice_id: number
        }
      >({
        query: (payload) => ({
          url: `/invoices/${payload.invoice_id}/member/${payload.member_id}`,
          method: "POST",
          body: payload,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Invoice", "Transaction", "Members","SysConfig","Facility"],
      }),

      swapLinkedMembers: builder.mutation<
        ApiResponse,
        {
          invoice_id: number;
          member_id: number;
          replacement_member_id: number;
          contract_id: number;
        }
      >({
        query: (payload) => ({
          url: `/member/${payload.member_id}/swap-membership`,
          method: "PUT",
          body: payload,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Invoice", "Transaction", "Members","SysConfig","Facility"],
      }),

      createInvoice: builder.mutation<ApiResponse, InvoiceDetails>({
        query: (invoiceData) => ({
          url: `/invoices`,
          method: "POST",
          body: invoiceData,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Invoice", "Transaction", "Members","SysConfig","Facility"],
      }),

      updateInvoice: builder.mutation<ApiResponse, InvoiceDetails>({
        query: (invoiceData) => ({
          url: `/invoices/${invoiceData.id}`,
          method: "PUT",
          body: invoiceData,
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Invoice", "Transaction", "Members","SysConfig","Facility"],
      }),

      updateInvoiceStatus: builder.mutation<ApiResponse, any>({
        query: (invoiceData) => ({
          url: `/invoices/${invoiceData.invoice_id}`,
          method: "PUT",
          body: invoiceData,
          header: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Invoice", "Transaction", "Members","SysConfig","Facility"],
      }),

      getInvoiceById: builder.query<InvoiceDetails, { id: number, org_id: number }>({
        query: ({ id, org_id }) => ({
          url: `/invoices/${id}?org_id=${org_id}`,
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }),
        keepUnusedDataFor: 5,
        providesTags: ["Invoice", "Transaction","SysConfig","Facility"],
      }),
      sendInvoiceByEmail: builder.mutation<ApiResponse, number>({
        query: (invoiceId) => ({
          url: `/invoices/${invoiceId}/send-email`,
          method: "POST",
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Invoice","SysConfig","Facility"],
      }),

      cancelInvoice: builder.mutation<ApiResponse, number>({
        query: (invoiceId) => ({
          url: `/invoices/${invoiceId}/cancel`,
          method: "DELETE",
          headers: {
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["Invoice", "SysConfig", "Facility", "Transaction", "Product", "Memberships"]
      }),
    };
  },
});

export const {
  useGetInvoicesQuery,
  useAddLinkedMembersMutation,
  useSwapLinkedMembersMutation,
  useGetLinkedMembersQuery,
  useCreateInvoiceMutation,
  useUpdateInvoiceMutation,
  useUpdateInvoiceStatusMutation,
  useGetInvoiceByIdQuery,
  useLazyGetInvoiceByIdQuery,
  useSendInvoiceByEmailMutation,
  useCancelInvoiceMutation,
} = Invoice;
